import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash-es';
import styled from 'styled-components';

import {
    CustomTable,
    CustomTableHeadersProps,
    MobileCardRowsProps,
    MobileTable,
    palette,
    EmptyState,
    Paper,
} from '@dev/ee-components';
import { DeliveryLogType, StatusMessageCategory } from '@dev/ee-api/ee-api-models';

import { ApplicationState } from '@/store';
import { SetEmailMsgid } from '@/store/emails/actions';
import useMobile from '@/services/hooks/useMobile';

import { useTableRows } from '../../hooks/useTableRows';
import { useTableHeaders } from '../../hooks/useTableHeaders';

const TableContainer = styled.div`
    margin-top: 32px;
`;

const MobileTableWrapper = styled.div`
    margin-top: 8px;
    & .MuiContainer-root.MuiContainer-maxWidthLg {
        padding: 0 20px;
    }
    & .MuiGrid-item {
        font-weight: 400;
    }
`;

interface TableComponentInterface {
    onStatusFilterClick?: (selected: (DeliveryLogType | StatusMessageCategory)[]) => void;
}

const TableComponent: FC<TableComponentInterface> = ({ onStatusFilterClick }: TableComponentInterface): JSX.Element => {
    const { reports } = useSelector((state: ApplicationState) => state);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();

    const headers = useTableHeaders(onStatusFilterClick);
    const rows = useTableRows();

    const columnWidth: Record<string, string> = {
        to: '45%',
        logsDate: '15%',
        emptyColumn: '25px',
        event: '130px',
        bounce: '130px',
        icon: '40px',
    };

    if (isMobile && isEmpty(reports?.eventLogByChannelName) && !reports?.eventLogByChannelNameLoading) {
        return (
            <Paper>
                <EmptyState background={palette.basic.paper} text={t('emptystate')} />
            </Paper>
        );
    }

    if (isMobile) {
        return (
            <MobileTableWrapper>
                <MobileTable
                    rows={rows as MobileCardRowsProps[]}
                    headers={headers as string[]}
                    onClickButton={(e, recipient): void => {
                        dispatch(SetEmailMsgid(recipient.id));
                    }}
                    buttonText={t('viewdetails')}
                    isWrapTextEnabled
                    expandedRows={
                        {
                            to: true,
                        } as any
                    }
                />
            </MobileTableWrapper>
        );
    }

    return (
        <TableContainer>
            <CustomTable
                headers={headers as CustomTableHeadersProps[]}
                rows={rows}
                isEmpty={isEmpty(reports?.eventLogByChannelName) && !reports?.eventLogByChannelNameLoading}
                loading={false}
                stickyHeader={false}
                rowPadding={48}
                tableCellPadding="20px 0"
                rowHeight={60}
                hideCheckboxes
                columnsWidth={columnWidth}
                emptyStateProps={{ text: t('thereisnodatatodisplay') }}
                disableRowClick
                disableFirstRowBorderTop
                verticalAlign="top"
            />
        </TableContainer>
    );
};

export default TableComponent;
